import { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    addCoverage,
    checkCoverage,
    coveragesSelector,
    fetchRespondedAlerts,
    resolveAlertByLink,
} from 'store/company/coverage';
import { useForm } from 'react-hook-form';
import styles from 'styles/modules/SidescreenAddMediaC.module.scss';
import Input from './addMediaCoverage/Input';
import Description from './addMediaCoverage/Description';
import Details from './addMediaCoverage/Details';
import Divider from 'components/Divider';
import Button from 'components/Button';
import CoveragePreview from './addMediaCoverage/CoveragePreview';
import { coverageFormDetails } from 'utils/coverage-form';
import ArrowDown from 'components/Svg/ArrowDown.js';
import { common, profile } from 'lang/en.json';
import SidescreenHeader from './SidescreenHeader';
import Typo from 'components/Typo';
import OptionSelect from 'components/OptionSelect';

const AddMediaCoverage = ({ onUpdate, title }) => {
    const dispatch = useDispatch();
    const { respondedAlerts } = useSelector(coveragesSelector);
    const { register, handleSubmit, errors } = useForm();
    const [isLoaded, setIisLoaded] = useState(false);
    const [isUrlSet, setIsUrlSet] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [mediaFetched, setMediaFetched] = useState(false);
    const [alertsSelected, setAlertsSelected] = useState([]);
    const [alertLink, setAlertLink] = useState('');
    const [alertLinkError, setAlertLinkError] = useState(null);
    // Pending link lookup, resolving to its alert id (null on error). A newer
    // link replaces it, so a late response is ignored.
    const alertLinkRequest = useRef(null);
    const submitting = useRef(false);

    // Bumped once per completed scrape, to remount the manual fields — they read
    // their props only on mount. It must never track what is being typed, or the
    // field remounts on every keystroke and loses focus.
    const [scrapeSeq, setScrapeSeq] = useState(0);

    const [formData, setFormData] = useState({
        details: {
            title: '',
            source: '',
            url: '',
        },
        description: '',
        title: '',
        publisher: '',
        coverageUrl: '',
    });

    useEffect(() => {
        dispatch(fetchRespondedAlerts());
    }, []);

    const handleDetails = (details) => {
        setFormData({ ...formData, details });
    };

    const handleDescription = (description) => {
        setFormData({ ...formData, description });
    };

    const handleUrl = async (coverageUrl) => {
        if (!coverageUrl) {
            return null;
        }
        setIisLoaded(false);
        setIsUrlSet(coverageUrl);

        const result = await dispatch(checkCoverage(coverageUrl));
        const scraped = checkCoverage.fulfilled.match(result)
            ? result.payload
            : null;

        // Seed the form from the thunk result rather than from a `coverage` effect.
        // The manual fields only read their props when they mount, so they are
        // remounted by `scrapeSeq` below — and an effect would land a render later
        // than that remount, which would remount them with an empty form and then
        // never reach them again. Setting both here keeps them in one commit.
        //
        // `scraped` is null when the outlet blocked us, which clears the previous
        // article rather than leaving it to be saved against this URL.
        setFormData((prev) => coverageFormDetails(prev, coverageUrl, scraped));
        setMediaFetched(!!scraped);
        setScrapeSeq((n) => n + 1);
        setIisLoaded(true);
    };

    const addAlert = ({ id, value }) =>
        setAlertsSelected((alerts) =>
            alerts.some((alert) => alert.id === id)
                ? alerts
                : [...alerts, { id, value }]
        );

    const removeAlert = (id) =>
        setAlertsSelected((alerts) =>
            alerts.filter((alert) => alert.id !== id)
        );

    const handleAlertLink = (link) => {
        if (!link.trim()) {
            alertLinkRequest.current = null;
            setAlertLinkError(null);
            return;
        }
        const request = dispatch(resolveAlertByLink(link)).then((result) => {
            if (alertLinkRequest.current !== request) {
                return null;
            }
            // Kept pending on error: Continue must not publish while the link
            // the admin pasted shows as not found.
            if (resolveAlertByLink.rejected.match(result)) {
                setAlertLinkError(
                    profile.media_coverage.sidebar.coverage_alert_link_error
                );
                return null;
            }
            alertLinkRequest.current = null;
            setAlertLink('');
            setAlertLinkError(null);
            addAlert({ id: result.payload.id, value: result.payload.title });
            return result.payload.id;
        });
        alertLinkRequest.current = request;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (submitting.current) {
            return;
        }
        submitting.current = true;
        // Clicking Continue blurs the link field first, so its lookup may still be
        // running and missing from this render's alertsSelected.
        const linkedId = alertLinkRequest.current
            ? await alertLinkRequest.current
            : undefined;
        if (linkedId === null) {
            submitting.current = false;
            return;
        }
        const alertIds = [
            ...new Set([...alertsSelected.map(({ id }) => id), linkedId]),
        ].filter(Boolean);
        dispatch(
            addCoverage({
                publisher: formData.details.source,
                title: formData.details.title,
                url: formData.details.url,
                description: formData.description,
                // A single alert keeps the request shape older backends accept.
                ...(alertIds.length > 1
                    ? { alertIds }
                    : { alertId: alertIds[0] }),
            })
        );
        if (onUpdate) {
            onUpdate('Media Coverage');
        }
    };

    const alertOptions = (respondedAlerts || [])
        .filter(({ id }) => !alertsSelected.some((alert) => alert.id === id))
        .map(({ id, title }) => ({ id, name: title }));

    const showManualEdit = () => {
        setIsEditable(!isEditable);
        setIsUrlSet(true);
    };

    return (
        <Fragment>
            <SidescreenHeader
                title={title}
                closeFn={() => {
                    setMediaFetched(false);
                    setIsUrlSet(false);
                }}
            />

            <form
                onSubmit={(e) => handleSubmit(handleFormSubmit(e))}
                autoComplete="off"
            >
                <div className={['mb-l', styles.inputWrapper].join(' ')}>
                    <Input
                        callback={(val) => handleUrl(val)}
                        register={register}
                        errors={errors}
                        description={formData.description}
                    />
                    {isUrlSet && (
                        <CoveragePreview
                            title={formData.details.title}
                            body={formData.description}
                            source={formData.details.source}
                            isLoaded={isLoaded}
                            isEditable={isEditable}
                            isUrlEmpty={isUrlSet}
                        />
                    )}
                </div>

                <Typo
                    variant={'small'}
                    className={[
                        styles.toggle,
                        isEditable ? styles.isActive : null,
                    ].join(' ')}
                    onClick={showManualEdit}
                >
                    {profile.media_coverage.sidebar.manual}
                    <ArrowDown />
                </Typo>

                <div
                    className={[
                        styles.dropdown,
                        isEditable ? styles.isActive : null,
                    ].join(' ')}
                >
                    {(mediaFetched || isEditable) && (
                        <div key={scrapeSeq}>
                            <Details
                                callback={(val) => handleDetails(val)}
                                register={register}
                                errors={errors}
                                data={formData.details}
                            />
                            <Description
                                callback={(val) => handleDescription(val)}
                                register={register}
                                errors={errors}
                                description={formData.description}
                            />
                            <div
                                className={['mb-m', styles.alertPicker].join(
                                    ' '
                                )}
                            >
                                <Typo variant={'small'} className={'mb-xs'}>
                                    {
                                        profile.media_coverage.sidebar
                                            .coverage_alert
                                    }
                                </Typo>
                                {alertsSelected.map(({ id, value }) => (
                                    <div
                                        key={`coverage-alert-${id}`}
                                        className={styles.alertResolved}
                                    >
                                        <Typo variant={'small'}>{value}</Typo>
                                        <button
                                            type={'button'}
                                            className={styles.alertRemove}
                                            aria-label={
                                                profile.media_coverage.sidebar
                                                    .coverage_alert_remove
                                            }
                                            onClick={() => removeAlert(id)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                {alertOptions.length > 0 && (
                                    <OptionSelect
                                        options={alertOptions}
                                        value={{
                                            value: profile.media_coverage
                                                .sidebar.coverage_alert_add,
                                        }}
                                        setValue={addAlert}
                                    />
                                )}
                                <input
                                    type={'text'}
                                    className={styles.alertLink}
                                    placeholder={
                                        profile.media_coverage.sidebar
                                            .coverage_alert_link
                                    }
                                    value={alertLink}
                                    onChange={(e) => {
                                        setAlertLink(e.target.value);
                                        setAlertLinkError(null);
                                    }}
                                    onBlur={(e) =>
                                        handleAlertLink(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAlertLink(e.target.value);
                                        }
                                    }}
                                />
                                {alertLinkError && (
                                    <Typo
                                        variant={'small'}
                                        className={styles.alertLinkError}
                                    >
                                        {alertLinkError}
                                    </Typo>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {(mediaFetched || isEditable) && (
                    <Fragment>
                        <Divider />
                        <Button
                            title={common.continue}
                            sticky={{ position: 'bottom' }}
                        />
                    </Fragment>
                )}
            </form>
        </Fragment>
    );
};

export default AddMediaCoverage;
